import { XIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  Input,
  LoadingErrorState,
  PageSkeleton,
} from "../../../components";
import { useApi, useAuth, useTranslations } from "../../../contexts";
import { client } from "../../../helpers";
import { AsyncStatus } from "../../../helpers/asyncStatus";
import { routerPaths } from "../../routerPaths";

export const SEOTab: React.FC = () => {
  const { siteId } =
    useParams<Parameters<typeof routerPaths.siteManage>["0"]>();
  if (!siteId) throw new Error(`no siteId in url params`);
  const [status, setStatus] = useState<AsyncStatus>("idle");
  const [keywords, setKeywords] = useState<string>("");
  const [keywordInput, setKeyworkInput] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const api = useApi();
  const siteQuery = api.siteById({ siteId });
  const site = siteQuery?.data;
  const i18n = useTranslations();
  const isLoading = status === "loading";
  const lastAttemptedKeywordsRef = useRef<string | null>(null);

  useEffect(() => {
    if (site?.seo_keywords) {
      setKeywords(site.seo_keywords.join(","));
    }
  }, [site?.seo_keywords]);

  const updateKeywords = async (newKeywords: string) => {
    if (isLoading) return;
    lastAttemptedKeywordsRef.current = newKeywords;
    setStatus("loading");
    setError(null);
    try {
      const results = await (
        await client.api.sites[":siteId"].$patch({
          json: { seo_keywords: newKeywords.split(",") },
          header: { authorization: `Bearer ${auth.token}` },
          param: { siteId },
        })
      ).json();
      if (!results.success) {
        setStatus("error");
        setError(i18n["Something went wrong"]);
        toast.error(i18n["Something went wrong"]);
        console.error(results.error.issues[0].message);
      } else {
        setStatus("success");
        lastAttemptedKeywordsRef.current = null;
        setKeywords(newKeywords);
        setKeyworkInput("");
        api.queryClient.invalidateQueries({
          queryKey: ["site", siteId],
        });
      }
    } catch (error) {
      setStatus("error");
      setError(i18n["Something went wrong"]);
      toast.error(i18n["Something went wrong"]);
      console.error(error);
    }
  };

  if (!siteQuery || siteQuery.isPending || siteQuery.isLoading) {
    return (
      <PageSkeleton
        cards={1}
        ariaLabel={i18n["Loading page content"]}
        loadingLabel={i18n["Loading..."]}
      />
    );
  }
  if (siteQuery.isError) {
    return (
      <LoadingErrorState
        message={i18n["Failed to load SEO settings."]}
        retryLabel={i18n["Retry"]}
        onRetry={() => siteQuery.refetch()}
      />
    );
  }
  if (!site) {
    return (
      <LoadingErrorState
        message={i18n["Could not load SEO settings."]}
        retryLabel={i18n["Retry"]}
        onRetry={() => siteQuery.refetch()}
      />
    );
  }

  return (
    <div id="site-manage-seo-tab" aria-busy={isLoading || siteQuery.isFetching}>
      <div className="flex flex-wrap gap-2">
        {keywords
          .split(",")
          .filter((a) => a)
          .map((keyword) => (
            <button
              key={keyword}
              className="bg-slate-200 p-2 ps-4 flex items-center cursor-pointer rounded hover:bg-slate-400"
              disabled={isLoading}
              onClick={(event) => {
                event.preventDefault();
                updateKeywords(
                  keywords
                    .split(",")
                    .filter((k) => k !== keyword)
                    .join(",")
                );
              }}
            >
              {keyword}
              <div className="ms-2  p-2">
                <XIcon className="size-3 " />
              </div>
            </button>
          ))}
      </div>
      {keywords && <div className="mt-5"></div>}

      <form
        className="flex items-center h-full"
        onSubmit={(event) => {
          event.preventDefault();
          updateKeywords(`${keywords},${keywordInput}`);
        }}
      >
        <Input
          variant="outlined"
          type="text"
          placeholder={i18n["Keyword"]}
          value={keywordInput}
          onChange={(event) => setKeyworkInput(event.target.value)}
        />
        <Button
          className="py-3 ms-4"
          type="submit"
          loading={isLoading}
          disabled={!keywordInput.trim() || isLoading}
          label="add"
        />
      </form>
      {error && (
        <LoadingErrorState
          className="mt-4"
          message={error}
          retryLabel={i18n["Retry"]}
          onRetry={() => {
            const attemptedKeywords = lastAttemptedKeywordsRef.current;
            if (attemptedKeywords !== null) {
              updateKeywords(attemptedKeywords);
            }
          }}
        />
      )}
      <p className="mt-4">{i18n["seoDescriptionMessage"]}</p>
    </div>
  );
};
